import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { useCurrentUser, useLogout } from "@/features/auth";
import { useVenueProfile } from "@/features/venues/model/useVenueProfile";
import {
  VENUE_ONBOARDING_STEPS,
  type VenueOnboardingStep,
} from "@/features/venue-onboarding/types";
import { BusinessStep } from "@/features/venue-onboarding/ui/BusinessStep";
import { CategoryStep } from "@/features/venue-onboarding/ui/CategoryStep";
import { LogoStep } from "@/features/venue-onboarding/ui/LogoStep";
import { ReviewStep } from "@/features/venue-onboarding/ui/ReviewStep";
import { StepIndicator } from "@/features/venue-onboarding/ui/StepIndicator";
import { WelcomeStep } from "@/features/venue-onboarding/ui/WelcomeStep";

export function VenueOnboardingPage() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const { data: profile, isLoading } = useVenueProfile();
  const [step, setStep] = useState<VenueOnboardingStep>("WELCOME");

  // Resume at the first incomplete step on first load.
  useEffect(() => {
    if (!profile) return;
    if (step !== "WELCOME") return;
    // Brand-new profile (business_name is set at signup, so address is the
    // first BUSINESS-step field that confirms the wizard has been touched)
    // → keep WELCOME so the venue gets the intro.
    if (!profile.address) return;
    if (!profile.contact_phone) {
      setStep("BUSINESS");
    } else if (!profile.category) {
      setStep("CATEGORY");
    } else if (!profile.logo_url) {
      setStep("LOGO");
    } else {
      setStep("REVIEW");
    }
    // We only want to seed once on profile arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-copy-muted">Loading…</p>
      </div>
    );
  }
  if (!profile) {
    return <Navigate to="/login" replace />;
  }
  if (profile.onboarding_completed_at) {
    return <Navigate to="/venue" replace />;
  }

  const goNext = () => {
    const idx = VENUE_ONBOARDING_STEPS.indexOf(step);
    if (idx < VENUE_ONBOARDING_STEPS.length - 1) {
      setStep(VENUE_ONBOARDING_STEPS[idx + 1]);
    }
  };
  const goBack = () => {
    const idx = VENUE_ONBOARDING_STEPS.indexOf(step);
    if (idx > 0) setStep(VENUE_ONBOARDING_STEPS[idx - 1]);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-6 py-4">
          <h1 className="text-base font-semibold tracking-display-tight text-copy">
            OneShift · Venue setup
          </h1>
          <button
            type="button"
            onClick={logout}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-copy-muted hover:bg-surface-sunken"
          >
            Sign out
          </button>
        </div>
        <div className="mx-auto max-w-2xl px-6 pb-4">
          <StepIndicator current={step} />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-8">
        <section className="rounded-lg border border-border bg-surface p-6 shadow-card">
          {step === "WELCOME" && <WelcomeStep email={user?.email} onNext={goNext} />}
          {step === "BUSINESS" && (
            <BusinessStep profile={profile} onSaved={goNext} onBack={goBack} />
          )}
          {step === "CATEGORY" && (
            <CategoryStep profile={profile} onSaved={goNext} onBack={goBack} />
          )}
          {step === "LOGO" && (
            <LogoStep profile={profile} onSaved={goNext} onBack={goBack} />
          )}
          {step === "REVIEW" && (
            <ReviewStep profile={profile} email={user?.email} onBack={goBack} />
          )}
        </section>
      </main>
    </div>
  );
}
